<template>
  <div
    role="region"
    tabindex="-1"
    class='vimeo'
    :aria-label="ariaToRead"
    @mousemove="handleMouseMove"
  >
    <div
      :id="elementId"
      :class="['player', aspectRatioClass]"
      ref="vimeo"
    />

    <VimeoCustomControls
      v-if="isVimeoControls"
      ref="controls"
      :show-controls="showControls"
      :support-full-screen="supportFullScreen"
      :is-playing="isPlaying"
      :show-play="showPlay"
      :buffer="buffer"
      :progress="progress"
      :duration="duration"
      :time="time"
      :is-muted="isMuted"
      :is-full-screen="isFullScreen"
      @handleAction="handleControlsAction"
      @seek="onSeek"
      @unmute="unmute"
      @mute="setVolume(0)"
      @minimize="minimize"
      @expand="expand"
    />
  </div>
</template>

<script>
import Player from '@vimeo/player'
import VimeoCustomControls from './custom-controls'
import window from '~/utils/window'

const eventsToEmit = [
  'play',
  'pause',
  'timeupdate',
  'progress',
  'seeked',
  'texttrackchange',
  'cuechange',
  'cuepoint',
  'volumechange',
  'loaded'
]

export default {
  components: {
    VimeoCustomControls
  },
  props: {
    url: {
      type: String,
      required: true
    },
    hiddenText: {
      type: String,
      required: true
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    },
    playerOpts: {
      type: Object,
      default: () => ({})
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    mute: {
      type: Boolean,
      default: false
    },
    skipToTime: {
      type: Number
    },
    defaultControls: {
      type: Boolean,
      default: false
    },
    aspectRatio: {
      type: String
    }
  },

  data () {
    return {
      player: null,
      elementId: this.url.replace(/[^a-zA-Z0-9 ]/g, ''),
      pauseTimer: null,
      pauseTimeout: 3000,
      isPlaying: false,
      hasPlayed: false,
      isMuted: this.mute,
      isFullScreen: false,
      showControls: true,
      showPlay: true,
      time: 0,
      duration: 0,
      buffer: 0,
      progress: 0
    }
  },

  mounted () {
    const options = {
      url: this.url,
      quality: '1080p',
      loop: this.loop,
      autoplay: this.autoplay,
      width: this.width,
      height: this.height,
      controls: false
    }

    // blocked the player from tracking any session data in the autoplay mode
    if (this.autoplay) {
      options.dnt = true
    }

    // Safe guarding against the error "You must pass either a valid element or a valid id."
    if (window.document.getElementById(this.elementId)) {
      this.player = new Player(this.elementId, { ...options, ...this.playerOpts })
      this.setEvents()
    }
  },

  beforeDestroy () {
    if (this.player) {
      this.player.unload()
    }
  },

  computed: {
    ariaToRead () {
      return `${this.$t('VIDEO_PLAYER_LABEL')} ${this.hiddenText}`
    },

    isVimeoControls () {
      return !this.autoplay
    },

    supportFullScreen () {
      return !!(document.documentElement.requestFullScreen
        || document.documentElement.mozRequestFullScreen
        || document.documentElement.webkitRequestFullscreen
        || document.documentElement.msRequestFullscreen)
    },

    aspectRatioClass () {
      switch (this.aspectRatio) {
        case '1/1': return 'aspect-1-1'
        case '3/4': return 'aspect-3-4'
        case '4/3': return 'aspect-4-3'
        case '5/3': return 'aspect-5-3'
        case '9/16': return 'aspect-9-16'
        case '16/9': return 'aspect-16-9'
        default: return ''
      }
    }
  },

  methods: {
    /**
     * Loads a new video ID.
     * Returns a promise
     * @param {Number} videoId
     * @return {LoadVideoPromise}
     */
    update (videoId) {
      return this.player.loadVideo(videoId)
    },

    play () {
      this.isPlaying = true
      return this.player.play()
    },

    pause () {
      this.isPlaying = false
      return this.player.pause()
    },

    setVolume (volume) {
      if (volume === 0) {
        this.isMuted = true
      }
      return this.player.setVolume(volume)
    },

    unmute (volume = 0.5) {
      this.isMuted = false
      return this.player.setVolume(volume)
    },

    expand () {
      if (this.$el.mozRequestFullScreen) {
        this.$el.mozRequestFullScreen()
      } else if (this.$el.webkitRequestFullscreen) {
        this.$el.webkitRequestFullscreen()
      } else if (this.$el.msRequestFullscreen) {
        this.$el.msRequestFullscreen()
      } else if (this.$el.requestFullScreen) {
        this.$el.requestFullscreen()
      }
      this.onFullScreenEvent()
    },

    minimize () {
      if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      } else if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    },

    onFullscreenChange  () {
      this.isFullScreen = !this.isFullScreen
      if (!this.isFullScreen) {
        this.offFullScreenEvent()
      }
    },

    onFullScreenEvent () {
      document.addEventListener('fullscreenchange', this.onFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.onFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.onFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
    },

    offFullScreenEvent () {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange)
      document.removeEventListener('mozfullscreenchange', this.onFullscreenChange)
      document.removeEventListener('MSFullscreenChange', this.onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
    },

    async setTime (time) {
      try {
        await this.player.setCurrentTime(time)
      } catch (error) {
        console.error('Vimeo setTime error', error)
        switch (error.name) {
          case 'RangeError':
            // the time was less than 0 or greater than the video’s duration
            break
          default:
            // some other error occurred
            break
        }
      }
    },

    handleMouseMove () {
      if (!this.$device.isMobile) {
        this.showControls = true
        if (this.isPlaying) {
          this.pauseEvent()
        }
      }
    },

    pauseEvent () {
      clearTimeout(this.pauseTimer)
      this.showControls = true
      this.pauseTimer = setTimeout(() => {
        this.showControls = false
      }, this.pauseTimeout)
    },

    async handleReady () {
      if (this.mute) {
        this.setVolume(0)
      }

      if (this.autoplay) {
        this.isPlaying = true
      }

      if (this.skipToTime) {
        await this.setTime(this.skipToTime)
      }

      // disabled autopause for allowing autoplay multiple videos
      this.player.setAutopause(false)

      if (this.isVimeoControls) {
        this.duration = await this.player.getDuration()
      }

      this.$emit('ready')
    },

    handleEnded () {
      this.isPlaying = false
      this.showPlay = true

      this.$emit('ended', this.player)
    },

    async setEvents () {
      try {
        this.player.on('ended', () => this.handleEnded())
        this.player.on('timeupdate', ({ seconds, percent }) => {
          this.time = seconds
          this.progress = percent
        })
        this.player.on('progress', ({ percent }) => {
          this.buffer = percent
        })
        this.player.on('error', error => {
          console.error('Vimeo Error', error)
          this.$emit('error', this.player)
        })

        eventsToEmit.forEach(event => {
          this.player.on(event, data => {
            this.$emit(event, data, this.player)
          })
        })

        await this.player.ready()

        await this.handleReady()
      } catch (error) {
        console.error('Vimeo Error [Ready]', error)
      }
    },

    handleControlsAction () {
      if (this.isEditMode) return

      if (!this.isPlaying) {
        this.showPlay = false
        this.play()
        this.pauseEvent()
      } else if (!this.showControls && this.$device.isMobile) {
        this.showControls = true
      } else {
        this.pause()
      }
    },

    onSeek (time) {
      this.progress = time / this.duration
      this.setTime(time)
      if (!this.isPlaying) {
        this.pause()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vimeo {
  object-fit: cover;
  position: relative;
  width: 100%;
  height: 100%;

  &:fullscreen {
    background-color: $color-black;
  }

  ::v-deep iframe {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }
}

.player {
  will-change: auto;
  animation: fade $t*5;
  display: flex;
  min-width: 100%;
}

.aspect-1-1 {
  @include aspect-ratio(1, 1);
}

.aspect-3-4 {
  @include aspect-ratio(3, 4);
}

.aspect-4-3 {
  @include aspect-ratio(4, 3);
}

.aspect-5-3 {
  @include aspect-ratio(5, 3);
}

.aspect-9-16 {
  width: 100%;

  @include tablet {
    width: 33.3%;
  }

  @include aspect-ratio(9, 16);
}

.aspect-16-9 {
  @include aspect-ratio(16, 9);
}
</style>
